import { useTranslation } from 'react-i18next';
import { AdvanceBatchStatus } from '@/types';
import { batchStatusStyleMap, getLifecycleNodes } from './lifecycle';
import { formatDateByDayjs } from '@/utils';

interface BatchStatusPillProps {
  status: AdvanceBatchStatus;
  /** queued batches: FFC's expected processing date rendered inline under the pill */
  expectedProcessingDate?: string | null;
  /** partially_approved: count of excluded orders appended after the pill */
  excludedCount?: number;
  /** render the compact 5-dot lifecycle sequence under the pill (list rows) */
  showDots?: boolean;
}

/**
 * Advance batch status pill (DESIGN §3) + optional queued-ETA line and
 * compact lifecycle dots (v2.2). Dots are decorative (`aria-hidden`) — the
 * accessible status is the pill's text.
 */
function BatchStatusPill({
  status,
  expectedProcessingDate,
  excludedCount,
  showDots = false,
}: BatchStatusPillProps) {
  const { t } = useTranslation();
  const styles = batchStatusStyleMap[status] ?? {
    color: 'rgb(115,123,140)',
    background: 'rgba(115,123,140,0.12)',
  };

  const dotClass = (state: string) => {
    if (state === 'completed') return 'bg-[rgb(49,196,127)]';
    if (state === 'current') return 'bg-[rgb(237,224,74)] motion-safe:animate-pulse';
    if (state === 'rejected') return 'bg-[rgb(220,40,40)]';
    if (state === 'not_applicable') return 'border border-dashed border-[rgb(220,40,40)]/30 bg-transparent';
    return 'border border-solid border-[rgb(232,230,227)] bg-white';
  };

  return (
    <div className='flex flex-col items-start'>
      <div className='flex items-center gap-1.5'>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            fontSize: '11px',
            fontWeight: 600,
            borderRadius: '9999px',
            padding: '4px 10px',
            color: styles.color,
            background: styles.background,
            whiteSpace: 'nowrap',
          }}
        >
          {String(t(`advance_batch_status.${status}`))}
        </span>
        {status === 'partially_approved' && (excludedCount ?? 0) > 0 ? (
          <span className='text-[11px] text-[rgb(115,123,140)] whitespace-nowrap'>
            · {excludedCount} {String(t('advance_cod_excluded'))}
          </span>
        ) : null}
      </div>
      {status === 'queued' && expectedProcessingDate ? (
        <span className='text-[10px] text-[rgb(115,123,140)] mt-0.5'>
          {String(t('advance_cod_processing_on'))}{' '}
          <bdi>{formatDateByDayjs(expectedProcessingDate, 'ddd, D MMM')}</bdi>
        </span>
      ) : null}
      {showDots ? (
        <div className='flex items-center gap-1 mt-1' aria-hidden='true'>
          {getLifecycleNodes(status).map((node) => (
            <span key={node.key} className={`w-1.5 h-1.5 rounded-full ${dotClass(node.state)}`} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default BatchStatusPill;
